//replaces widget-component.html
import type { FC, ReactNode } from "react";

import classNames from "classnames";

import { EFamilyTypes } from "app/components/dashboard/dashboard-templates/components/DashboardTemplatesTypes";
import { DASHBOARD_ID_PREVIEW_MODE } from "app/components/dashboard/widget-wizard/constants";
import { TableNoData } from "app/components/table-no-data/TableNoData";
import type { IDashboardWidgetInterface } from "app/components/widget/utils/DashboardWidgetsInterfaces-v2";
import { WidgetState } from "app/components/widget/widget-types/Widget";
import { NewWidgetLoader } from "app/components/widget-loader/NewWidgetLoader";
import WidgetSoftLimitPlaceholder from "app/components/WidgetSoftLimitPlaceholder/WidgetSoftLimitPlaceholder";
import { SerpOverlay, SerpOverlayProvider } from "app/pages/digital-marketing/keywords-campaigns";

import { WidgetStateErrorMessage } from "./WidgetStateErrorMessage";

const NoWidgetCmp = ({ widget }) => {
    const { type } = widget;

    return (
        <h3 className={"noWidgetCmp"}>
            No Widget for {type} {widget.templateUrl} {typeof widget}
        </h3>
    );
};

const withRankTrackerSerpOverlay = (content: ReactNode, isRankTrackerWidget: boolean): ReactNode => {
    if (!isRankTrackerWidget) {
        return content;
    }

    return (
        <SerpOverlayProvider>
            {content}
            <SerpOverlay />
        </SerpOverlayProvider>
    );
};

export const WidgetComponentV2: FC<IDashboardWidgetInterface> = (props) => {
    const { widget, setChart } = props;
    const WidgetCmp = widget.templateComponent ?? NoWidgetCmp;
    const isRankTrackerWidget = widget.getWidgetModel().family === EFamilyTypes.RankTracker;

    function handleSetChart(chartRef) {
        setChart?.(chartRef);
    }

    const wrapperHeight =
        widget.widgetState < WidgetState.LOADED
            ? widget.viewData.loadingHeight
            : widget.widgetState === WidgetState.ERROR
              ? widget.viewData.errorHeight
              : widget.viewData.height;

    const content = (
        <div
            className={classNames(
                "swWidget-component grid-no-drag",
                widget.getWidgetModel().type,
                `unique_${widget.getWidgetModel().type}_${widget.getWidgetModel().metric}_${
                    widget.getWidgetModel().family
                }`,
                {
                    "swWidget-component-overflow": widget.viewOptions.showOverflow,
                    "top-margin": widget.viewOptions.preserveLegendSpace && widget.widgetState !== WidgetState.ERROR,
                    "swWidget-component--no-title": !widget.viewOptions.showTitle,
                },
                {
                    preview: widget?.dashboardId === DASHBOARD_ID_PREVIEW_MODE,
                },
            )}
            style={{ height: wrapperHeight }}
        >
            {/*Widget content*/}
            {!!WidgetCmp && widget.widgetState === WidgetState.LOADED ? (
                <div className="widget-content">
                    <WidgetCmp
                        widget={widget}
                        setChart={handleSetChart}
                    />
                </div>
            ) : null}
            {/*Widget loader*/}
            {widget.widgetState === WidgetState.LOADING && !widget.viewOptions.noLoading ? (
                <NewWidgetLoader widget={widget} />
            ) : null}
            {widget.widgetState === WidgetState.SOFT_LIMIT_REACHED ? <WidgetSoftLimitPlaceholder /> : null}
            {/*Widget error*/}
            {widget.widgetState === WidgetState.ERROR ? <WidgetStateErrorMessage widget={widget} /> : null}
            {/*Widget empty*/}
            {widget.widgetState === WidgetState.EMPTY || widget.widgetState === WidgetState.ASSET_NOT_ACCESSIBLE ? (
                <TableNoData config={widget.errorConfig} />
            ) : null}
        </div>
    );

    return withRankTrackerSerpOverlay(content, isRankTrackerWidget);
};
